/*
dtOptions:
        serverSide: boolean,
        rows: [], --array of {col1data:value,.....}
        sorts: [],
        hideSearch: boolean,
        title: string
        , headerButton: html
        ,columns: dtColumn[] --column definition

dtColumn:
    name: string, --column label
    data: string, 
    visible: boolean, 
    sortable: boolean, 
    searchable: boolean,
    cell:  (row, col)=>{...} this function must return html

*/

import { forwardRef, Fragment, useEffect, useImperativeHandle, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useLocation } from 'react-router-dom';

const DatatableWrapper = forwardRef((props, ref) => {
  const [rows, setRows] = useState(null);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [sort, setSort] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [checkStorage, setCheckStorage] = useState(false);
  const [toReload, setToReload] = useState(false);
  const [maxRows, setMaxRows] = useState(0);
  const [dtParams, setDtParams] = useState({
    draw: 0,
    columns: [],
    length: 10,
    start: 0,
    search: { value: null, regex: false },
    order: [{ dir: 'asc', column: 0 }],
  });

  const debounceTime = 500;

  const [searchString, setSearchString] = useState('');
  const [debouncedSearchValue] = useDebounce(searchString, debounceTime);

  const dtOptions = props.dtOptions;

  const location = useLocation();

  useImperativeHandle(ref, () => ({
    rerenderDatatable() {
      setToReload(true);
    },
  }));

  const getDataFromServer = () => {
    const dtPar = dtParams;
    dtPar.search.value =
      debouncedSearchValue === '' ? null : debouncedSearchValue;
    const columnIndex =
      dtPar.columns?.findIndex((x) => x.data === sort?.name) ?? 0;
    dtPar.order = [
      {
        dir: sort?.direction ?? 'asc',
        column: columnIndex < 0 ? 0 : columnIndex,
      },
    ];
    dtPar.start = (currentPage - 1) * pageSize;
    dtPar.length = pageSize;
    dtPar.draw++;
    setDtParams(dtPar);

    props.dtOptions
      .ajax(
        props.dtOptions.ajaxParams
          ? { input: dtParams, ...props.dtOptions.ajaxParams }
          : dtParams
      )
      .then((response) => {
        console.log('ajax done');
        setData(response);
      });
    return null;
  };

  const setData = (data) => {
    setMaxRows(data.recordsFiltered);
    if (pageSize !== -1) {
      const sIndex = (currentPage - 1) * pageSize;
      setStartIndex(sIndex);
      setEndIndex(sIndex + pageSize);

      //rowsTmp = rowsTmp?.slice(startIndex,endIndex)
    } else {
      setStartIndex(0);
      setEndIndex(data?.data?.length ?? 0);
    }

    if(props?.countNumberRows) {
      props.countNumberRows(data.recordsFiltered);
    }

    setRows([...data.data]); //tutte le righe senza paginazione
    console.log(data);
  };

  const pageSizeOptions = [
    //{ value: 5, label: '5 elementi per pagina' },
    { value: 10, label: '10 elementi per pagina' },
    { value: 20, label: '20 elementi per pagina' },
    { value: 50, label: '50 elementi per pagina' },
    { value: 100, label: '100 elementi per pagina' },
    { value: -1, label: 'tutti gli elementi' },
  ];
  const defaultPageSize = pageSizeOptions[0].value;

  const checkStorageData = () => {
    console.log('use effect on dtOptions, getOptionsLocalStorage=', getOptionsLocalStorage());
    const objStorage = getOptionsLocalStorage();

    const searchStringValue = objStorage?.searchString ?? '';
    const sortValue = objStorage?.sortObj;
    const pageSizeValue = objStorage?.pageSize ?? defaultPageSize;
    const currentPageValue = objStorage?.currentPage ?? 1;

    if (
      searchStringValue === searchString &&
      JSON.stringify(sortValue) === JSON.stringify(sort) &&
      pageSizeValue === pageSize &&
      currentPageValue === currentPage
    ) {
      return;
    }

    setSearchString(objStorage?.searchString ?? '');
    setSort(objStorage?.sortObj);
    setPageSize(objStorage?.pageSize ?? defaultPageSize);
    setCurrentPage(objStorage?.currentPage ?? 1);

    setTimeout(() => {
      setCheckStorage(true);
    }, debounceTime + 50);
  }

  //listener on dtOptions changes
  useEffect(() => {
    console.log('use effect dtwrapper', props.dtOptions);
    const dtPar = { ...dtParams };

    dtPar.columns = props.dtOptions?.columns
      ?.filter((x) => x.data)
      .map(({ name, data, sortable, searchable, search }) => ({
        data: data ?? 'null',
        name: data ? name : '',
        searchable: searchable,
        orderable: sortable,
        search: search ?? { value: '', regex: false }
      }));

    dtPar.length = props.dtOptions?.pageSize ?? defaultPageSize;

    dtPar.ajaxParams = props.dtOptions?.ajaxParams;

    setDtParams(dtPar);
    checkStorageData();

    const changingValues = JSON.stringify(dtPar) !== JSON.stringify(dtParams);
    if (dtPar?.columns && changingValues) {
      const changingFilters = dtParams.ajaxParams && JSON.stringify(dtPar.ajaxParams) !== JSON.stringify(dtParams.ajaxParams);
      if (changingFilters) {
        setCurrentPage(1);
        const option = { currentPage: 1 };
        setOptionsLocalStorage(option);
      }
      setToReload(true);
    }
  }, [props.dtOptions]);

  //listener on sort object changing, change pagesize
  useEffect(() => {
    reloadData();
  }, [sort, pageSize, currentPage, checkStorage, toReload]);

  useEffect(() => {
    setToReload(true);
  }, [debouncedSearchValue]);

  //click on sort column in html
  const doSort = (col) => {
    let direction = 'asc';
    if (!col.sortable) return;
    if (sort?.name === col.data) {
      direction = sort?.direction === 'asc' ? 'desc' : 'asc';
    }
    const sortObj = { name: col.data, direction: direction };
    setCurrentPage(1);
    setSort(sortObj);
    const option = { sortObj : sortObj, currentPage: 1 };
    setOptionsLocalStorage(option);
    setToReload(true);
  };

  const reloadData = () => {
    if (!checkStorage || !toReload) {
      return;
    }

    console.log('reload data');

    setToReload(false);
    setRows(null);

    if (props.dtOptions?.serverSide) return getDataFromServer();

    if (!props.dtOptions?.rows) return;

    let rowsTmp = [...props.dtOptions?.rows];

    //search string part
    const columns = props?.dtOptions?.columns?.filter((x) => x.searchable);
    rowsTmp = rowsTmp.filter((x) => {
      let ris = false;
      for (let property in x) {
        let col = columns.find((x) => x.prop === property);
        if (col && col['searchable'] === false) continue;
        if (
          x[property]?.toString().toLowerCase().includes(debouncedSearchValue)
        )
          ris = true;
      }
      return ris;
    });

    //sort part
    const moltiplicator = sort?.direction === 'desc' ? -1 : 1;
    rowsTmp?.sort(
      (a, b) => moltiplicator * (a[sort?.name] < b[sort?.name] ? -1 : 1)
    );
    let sIndex = 0;
    let eIndex = rowsTmp?.length ?? 0;
    //pagesize part
    if (pageSize !== -1) {
      sIndex = (currentPage - 1) * pageSize;
      eIndex = sIndex + pageSize;
    }
    setStartIndex(sIndex);
    setEndIndex(eIndex);
    setMaxRows(rowsTmp.length + 1);
    rowsTmp = rowsTmp.slice(sIndex, eIndex);
    setRows((rows) => [...rowsTmp]); //tutte le righe senza paginazione
  };

  // const clearOptionsLocalStorage = ()=>{
  //   sessionStorage.removeItem(`${location.pathname}_options`);
  //   setCurrentPage(1);
  // }

  const setOptionsLocalStorage = (option)=> {
    let obj = option;
    const optionsJson = getOptionsLocalStorage();
    if (optionsJson) obj = { ...optionsJson, ...option };
    sessionStorage.setItem(`${location.pathname}_options`,JSON.stringify(obj));    
  }

  const getOptionsLocalStorage = ()=>{
    const optionsJson = sessionStorage.getItem(`${location.pathname}_options`);
    if (optionsJson)return JSON.parse(optionsJson);
    return null;
  }

  const printRow = (row, rowIndex) => {
    return (
      <tr
        key={row.id || rowIndex}
        className='rtds-border-b rtds-border-gray-02 hover:bg-gray-100 rtds-border-r rtds-border-l'
      >
        {dtOptions?.columns
          ?.filter((x) => x.visible)
          .map((col, y) => {
            return printRowCell(row, y, col);
          })}
      </tr>
    );
  };

  const printRowCell = (row, colIndex, col) => {
    return (
      <Fragment key={col.id || colIndex}>
        {colIndex === 0 && (
          <th
            className={
              (col.center ? 'rtds-text-center' : '') +
              ' rtds-p-3 rtds-border-gray-04'
            }
          >
            {!col.cell && (
              <div className='rtds-justify-between rtds-flex'>
                <span className='lg:rtds-hidden rtds-font-semibold'>
                  {col.name}
                </span>
                <span>{printCellValue(row, col)}</span>
              </div>
            )}
            {col.cell ? col.cell(row, col) : null}
          </th>
        )}
        {colIndex !== 0 && (
          <td
            className={
              (col.center ? 'sm:rtds-text-left rtds-text-center' : '') +
              ' rtds-p-2 rtds-border-gray-02 sx-word-break '
            }
          >
            {!col.cell && (
              <div className='rtds-justify-between rtds-flex'>
                <span className='lg:rtds-hidden rtds-font-semibold'>
                  {col.name}
                </span>
                <span>{printCellValue(row, col)}</span>
              </div>
            )}
            {col.cell ? col.cell(row, col) : null}
          </td>
        )}
      </Fragment>
    );
  };

  const printCellValue = (row, col) => {
    if (col.cell) return null;
    return row[col.data] ?? '';
  };

  const printColumnHeader = (col, index) => {
    return (
      <th
        key={'col' + index}
        onClick={() => doSort(col)}
        id={'col' + index}
        sortable={col.sortable ? col.data : null}
        hidden={col.hidden}
        className={
          (col.center ? 'sm:rtds-text-left rtds-text-center' : '') +
          ' rtds-p-3 ' +
          (sort?.name === col.data
            ? sort?.direction === 'desc'
              ? 'desc'
              : 'asc'
            : '')
        }
      >
        {col.name}
      </th>
    );
  };

  const doChangePageSize = (value) => {
    setPageSize(parseInt(value));
    setCurrentPage(1);
    const option = { pageSize : parseInt(value), currentPage: 1 };
    setOptionsLocalStorage(option); 
    setToReload(true);
  };

  // manage search string
  const handleSearchStringChange = (event) => {
    const value = event.target.value;
    setSearchString(value);
    setCurrentPage(1);
    setOptionsLocalStorage({ searchString: value, currentPage: 1 });
  };

  // change current page
  const changePage = (newPage, event) => {
    event.preventDefault();
    console.log(newPage);
    if (newPage === currentPage) return;
    setCurrentPage(newPage);
    const option = { currentPage : newPage };
    setOptionsLocalStorage(option); 
    setToReload(true);
  };

  const renderPagination = () => {
    const showFirst = false;
    const showPrevious = true;
    const showNext = true;
    const showLast = false;

    const currentPageSize = pageSize;
    const mod = maxRows % currentPageSize > 0 ? 1 : 0;

    const pagesNumber =
      currentPageSize === -1 ? 1 : Math.floor(maxRows / currentPageSize) + mod;
    const case4Pages = [];
    for (let i = 1; i <= pagesNumber; i++) {
      //prima e ultima pagina e pagina corrente le stampo sempre
      if (
        i === 1 ||
        i === pagesNumber ||
        i === currentPage ||
        pagesNumber <= 4 /*se ho 4 pagine stampo sempre */ ||
        (currentPage <= 4 &&
          i === 2) /*pagina 2 la stampo se la pagina selezionata è <=4*/ ||
        i + 1 ===
          currentPage /*pagina currentpage-1 la stampo se la pagina selezionata è <=4*/ ||
        /*|| (i>4 && pagesNumber - currentPage<=4)*/
        (i > 4 && i === currentPage + 1)
      ) {
        case4Pages.push(
          <li key={'page' + i}>
            <a
              className={
                'rtds-pagination__link  rtds-pagination__link-button rtds-pagination__link-text ' +
                (currentPage === i
                  ? ' rtds-background-primary  rtds-border-primary rtds-text-white hover:rtds-content-01'
                  : '')
              }
              href='#'
              onClick={(e) => changePage(i, e)}
              aria-label={'pagina ' + i}
            >
              {i}
            </a>
          </li>
        );
        continue;
      }
      if (
        (i === 2 && currentPage > 4) ||
        (i === pagesNumber - 1 && currentPage < i - 1)
      ) {
        /*se sono a pagina 2 ed ho selezionato una pagina dalla 5 in su 
                    oppure sono la penultima pagina e ho selezionato una pagina minore della precedente
                    */
        /*stampo il grigio con i ... */
        case4Pages.push(
          <li key='pageDots'>
            <a
              className='rtds-pagination__link  rtds-pagination__link-button  rtds-background-01 rtds-content-disabled'
              aria-disabled='true'
              tabIndex='-1'
            >
              ...
            </a>
          </li>
        );
      }
    }

    return (
      <nav
        className='rtds-pagination'
        role='navigation'
        aria-label='Paginazione'
      >
        <ul className='rtds-pagination__list'>
          {showFirst && (
            <li key='changePageOne'>
              <a
                onClick={(e) => changePage(1, e)}
                className={
                  'rtds-pagination__link rtds-pagination__link-button ' +
                  (currentPage === 1
                    ? 'rtds-content-disabled rtds-background-01'
                    : '')
                }
                href='#'
                aria-label='Pagina iniziale'
                aria-disabled={currentPage === 1}
                tabIndex={currentPage === 1 ? -1 : 0}
              >
                <svg
                  className='rtds-icon rtds-fill-current rtds-w-4 rtds-h-5 md:rtds-w-5 md:rtds-h-5'
                  aria-hidden='true'
                  focusable='false'
                  role='img'
                >
                  <use
                    xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--chevron-double-left`}
                  />
                </svg>
              </a>
            </li>
          )}

          {showPrevious && (
            <li key='changePage'>
              <a
                className={
                  'rtds-pagination__link rtds-pagination__link-button ' +
                  (currentPage === 1
                    ? 'rtds-content-disabled rtds-background-01'
                    : '')
                }
                href='#'
                onClick={(e) =>
                  changePage(currentPage > 1 ? currentPage - 1 : 1, e)
                }
                aria-label='Pagina precedente'
                aria-disabled={currentPage === 1}
                tabIndex={currentPage === 1 ? -1 : 0}
              >
                <svg
                  className='rtds-icon rtds-fill-current rtds-w-4 rtds-h-5 md:rtds-w-5 md:rtds-h-5'
                  aria-hidden='true'
                  focusable='false'
                  role='img'
                >
                  <use
                    xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--chevron-left`}
                  />
                </svg>
              </a>
            </li>
          )}
          {case4Pages}

          {showNext && (
            <li key='nextPage'>
              <a
                className={
                  'rtds-pagination__link rtds-pagination__link-button ' +
                  (currentPage === pagesNumber
                    ? 'rtds-content-disabled rtds-background-01'
                    : '')
                }
                href='#'
                aria-label='Pagina successiva'
                aria-disabled={currentPage === pagesNumber}
                tabIndex={currentPage === pagesNumber ? -1 : 0}
                onClick={(e) =>
                  changePage(
                    currentPage < pagesNumber ? currentPage + 1 : pagesNumber,
                    e
                  )
                }
              >
                <svg
                  className='rtds-icon rtds-fill-current rtds-w-4 rtds-h-5 md:rtds-w-5 md:rtds-h-5'
                  aria-hidden='true'
                  focusable='false'
                  role='img'
                >
                  <use
                    xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--chevron-right`}
                  />
                </svg>
              </a>
            </li>
          )}
          {showLast && (
            <li key='changeSpecificPage'>
              <a
                className={
                  'rtds-pagination__link rtds-pagination__link-button ' +
                  (currentPage === pagesNumber
                    ? 'rtds-content-disabled rtds-background-01'
                    : '')
                }
                href='#'
                onClick={(e) => changePage(pagesNumber, e)}
                aria-label='Pagina finale'
                aria-disabled={currentPage === pagesNumber}
                tabIndex={currentPage === pagesNumber ? -1 : 0}
              >
                <svg
                  className='rtds-icon rtds-fill-current rtds-w-4 rtds-h-5 md:rtds-w-5 md:rtds-h-5'
                  aria-hidden='true'
                  focusable='false'
                  role='img'
                >
                  <use
                    xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--chevron-double-right`}
                  />
                </svg>
              </a>
            </li>
          )}
        </ul>
      </nav>
    );
  };

  return (
    <div className='rtds-m-4'>
      <div className='rtds-mb-4'>
        {dtOptions?.title && (
          <h2 className='rtds-text-2xl rtds-font-bold'>
            {dtOptions.title} {dtOptions.titleSecond}
          </h2>
        )}
      </div>
      <div
        className={
          (!dtOptions?.hideSearch ? 'rtds-justify-between' : 'justify-end') +
          ' rtds-flex rtds-mb-3 rtds-flex-wrap rtds-gap-3'
        }
      >
        {!dtOptions?.hideSearch && (
          <div className='rtds-inline-flex rtds-gap-1 self-center'>
            <div className='rtds-input-field rtds-gap-1 '>
              <label
                htmlFor='searchId'
                className='rtds-input-field__label rtds-sr-only'
              >
                Cerca
              </label>
              <div className='rtds-input'>
                {!searchString && (
                  <svg
                    className='rtds-icon rtds-fill-current rtds-w-6 rtds-h-6 rtds-absolute rtds-left-4 rtds-content-placeholder'
                    aria-hidden='true'
                    focusable='false'
                    role='img'
                  >
                    <use
                      xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--magnifying-glass`}
                    />
                  </svg>
                )}
                <input
                  id='searchId'
                  className='rtds-text-sm rtds-input-placeholder rtds-pl-12 '
                  type='search'
                  value={searchString}
                  onChange={handleSearchStringChange}
                />
              </div>
            </div>
            <button
              type='button'
              id='refreshBtn'
              className='rtds-btn--secondary rtds-btn rtds-btn--s rtds-btn--icon-left rtds-group/button'
              onClick={() => setToReload(true)}
              hidden={!props.reloadDatatable}
            >
              <svg
                className='rtds-icon rtds-fill-current rtds-w-5 rtds-h-5 rtds-transition-all group-hover/button:-rtds-translate-x-1'
                aria-hidden='true'
                focusable='false'
                role='img'
              >
                <use
                  xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#solid--arrow-path`}
                />
              </svg>
              Aggiorna&nbsp;elenco
            </button>
          </div>
        )}

        {props.headerButton}
      </div>

      <div>
        <table className='min-w-full rtds-bg-white table-auto table'>
          <thead>
            <tr className='rtds-background-03'>
              {dtOptions?.columns
                ?.filter((x) => x.visible)
                .map((col, i) => {
                  return printColumnHeader(col, i);
                })}
            </tr>
          </thead>

          <tbody className='rtds-content-01 rtds-text-sm'>
            {
              rows?.map((row, i) => printRow(row, i))
            }

            <tr>
              <td colSpan={dtOptions?.columns?.length}>
                {
                  !rows &&
                  <div
                    className='rtds-loading-status'
                    aria-live='polite'
                    aria-busy='true'
                    role='status'
                  >
                    <div className='rtds-loading-results rtds-grid rtds-font-bold rtds-text-center rtds-gap-8 rtds-justify-center rtds-justify-items-center rtds-py-20'>
                      <svg
                        className='rtds-icon rtds-fill-current rtds-w-12 rtds-h-12 rtds-animate-spin'
                        aria-hidden='true'
                        focusable='false'
                        role='img'
                      >
                        <use
                          xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#spinner`}
                        />
                      </svg>

                      <h2 className='rtds-heading-3'>Caricamento risultati...</h2>
                    </div>
                  </div>
                }

                {
                  rows && rows.length === 0 &&
                  <div
                    className='rtds-empty-state rtds-grid rtds-gap-4'
                    aria-live='assertive'
                    aria-atomic='true'
                  >
                    <div className='rtds-font-bold rtds-text-center rtds-grid rtds-gap-8 rtds-justify-center rtds-justify-items-center rtds-py-20'>
                      <svg
                        className='rtds-icon rtds-fill-current rtds-w-16 rtds-h-16 rtds-content-03'
                        aria-hidden='true'
                        focusable='false'
                        role='img'
                      >
                        <use
                          xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#outline--exclamation-circle`}
                        />
                      </svg>

                      <h3 className='rtds-heading-4'>
                        Non ci sono risultati per questi criteri di ricerca
                      </h3>
                    </div>
                  </div>
                }
              </td>
            </tr>
          </tbody>
        </table>

        <div className='rtds-flex md:rtds-justify-between rtds-flex-wrap rtds-justify-center'>
        <div className='sx-align-self'>
          <p className='rtds-text-sm rtds-text-gray-700'>
            Totale elementi nella tabella: <strong>{maxRows}</strong>
          </p>
        </div>
          {renderPagination()}
          
          <div className='rtds-input-field rtds-select-field rtds-gap-1 rtds-p-2'>
            <label
              htmlFor='selectId'
              className='rtds-input-field__label rtds-sr-only'
            >
              Elementi per pagina
            </label>

            <div className='rtds-select rtds-select--sm'>
              <select
                id='selectId'
                className='rtds-pr-7'
                onChange={(choice) => doChangePageSize(choice.target.value)}
                value={pageSize}
              >
                {pageSizeOptions.map((item, i) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default DatatableWrapper;
